import { Badge } from "@/components/ui/badge";
import { MapPin, DoorOpen } from "lucide-react";
import { formatDateDisplay, formatTime, formatPrice } from "@/lib/utils/date-utils";
import { getBrowserTimezone, getTimezoneAbbreviation, timezonesHaveSameOffset } from "@/lib/utils/timezone-utils";
import type { ClassStatus } from "@/lib/api/classes";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { storage } from "@/lib/storage";
import { cn } from "@/lib/utils/general";
import { ClassCardOverlayBadge, pickOverlayBadge } from "./class-card-overlay-badge";

/** Build a Google Maps URL for the given address */
function buildMapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

interface ClassMetadataProps {
  /** Start time - required unless customDateTimeString is provided */
  startTime?: Date;
  /** End time - required unless customDateTimeString is provided */
  endTime?: Date;
  timezone?: string;
  title: string;
  location: string;
  /** Optional street address */
  address?: string | null;
  /** Optional room/studio name */
  room?: string | null;
  price?: number | null;
  /** Optional custom price string (e.g. "$25 - $60"). When provided, overrides price number formatting. */
  customPriceString?: string;
  status?: ClassStatus;
  showTimezone?: boolean;
  hasMenu?: boolean; // For ClassCard with menu button
  /** Optional content to render right after the title (e.g., collaborator badge) */
  afterTitle?: ReactNode;
  /** Visual variant - "light" for overlay on dark backgrounds (photos) */
  variant?: "default" | "light";
  /** Optional pre-formatted date/time string. When provided, overrides startTime/endTime computation. */
  customDateTimeString?: string;
  /** Optional studio info — when present, replaces location badge with studio badge */
  studio?: { id: string; name: string; handle: string | null; logo_url: string | null } | null;
  /** Hide the status overlay badge (e.g., sold out, waitlist) */
  hideStatusBadge?: boolean;
  /** Hide location/studio/address badges (e.g. when on the studio's own page) */
  hideLocation?: boolean;
  /** Hide the price badge entirely */
  hidePrice?: boolean;
  /**
   * Current viewer's registration state. When present and active, surfaces a
   * REGISTERED / UNPAID badge instead of the class-level SOLD OUT / WAITLIST.
   */
  userRegistration?: { isPaid: boolean; isOnWaitlist: boolean } | null;
}

export function ClassMetadata({
  startTime,
  endTime,
  timezone,
  title,
  location,
  address,
  room,
  price,
  customPriceString,
  status = "open",
  showTimezone = true,
  hasMenu = false,
  afterTitle,
  variant = "default",
  customDateTimeString,
  studio,
  hideStatusBadge = false,
  hideLocation = false,
  hidePrice = false,
  userRegistration,
}: ClassMetadataProps) {
  const isLight = variant === "light";
  const badgeKind = hideStatusBadge
    ? null
    : pickOverlayBadge(status, userRegistration);

  // Build formatted date/time string - use custom string if provided, otherwise compute from start/end times
  let formattedDateTime: string;
  if (customDateTimeString) {
    formattedDateTime = customDateTimeString;
  } else if (startTime && endTime) {
    // Format date and time
    const dateStr = formatDateDisplay(startTime);
    const startTimeStr = formatTime(startTime);
    const endTimeStr = formatTime(endTime);

    // Get user's timezone and compare with class timezone
    // Use offset comparison to handle different timezone names with same offset (e.g., America/New_York vs US/Eastern)
    const userTimezone = getBrowserTimezone();
    const isSameTimezone = timezone ? timezonesHaveSameOffset(timezone, userTimezone, startTime) : true;
    const shouldShowTimezone = showTimezone && timezone && !isSameTimezone;
    const timezoneAbbr = shouldShowTimezone ? getTimezoneAbbreviation(timezone) : "";

    formattedDateTime = shouldShowTimezone
      ? `${dateStr.toUpperCase()} • ${startTimeStr} - ${endTimeStr} ${timezoneAbbr}`
      : `${dateStr.toUpperCase()} • ${startTimeStr} - ${endTimeStr}`;
  } else {
    formattedDateTime = "";
  }

  // Format price - use custom string if provided, otherwise format from number
  const priceStr = customPriceString ?? formatPrice(price);

  return (
    <>
      {/* Date and Time + status overlay badge */}
      {(formattedDateTime || badgeKind) && (
        <div className="flex items-center justify-between gap-2">
          {formattedDateTime && (
            <p className={`font-mono text-xs ${isLight ? "text-white/80" : "text-on-dark-muted"}`}>
              {formattedDateTime}
            </p>
          )}
          {badgeKind && <ClassCardOverlayBadge kind={badgeKind} />}
        </div>
      )}

      {/* Title and optional content after it */}
      <div className={hasMenu ? "pr-8" : ""}>
        <h3
          className={`text-xl font-bold tracking-tight ${isLight ? "text-white" : "text-on-dark"} ${
            status === "canceled" ? "line-through opacity-75" : ""
          }`}
          data-testid="class-card-title"
        >
          {title}
        </h3>
        {afterTitle && <div className="flex items-center">{afterTitle}</div>}
      </div>

      {/* Location or Studio Badge */}
      <div className="flex flex-wrap gap-2">
        {!hideLocation && (studio ? (
          <Link
            to={`/studios/${studio.id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex group/studio"
          >
            <Badge variant={isLight ? "outline" : "secondary"} className={cn(
              "gap-1.5 transition-colors duration-200 cursor-pointer",
              isLight ? "border-white/30 text-white/80 hover:text-white" : "text-on-dark-muted hover:text-on-dark"
            )}>
              <Avatar className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover/studio:scale-110">
                {studio.logo_url && (
                  <AvatarImage
                    src={studio.logo_url.startsWith("http") ? studio.logo_url : storage.getThumbnailUrl(studio.logo_url, { width: 32, height: 32, quality: 80 })}
                    alt={studio.name}
                  />
                )}
                <AvatarFallback className={cn("text-[8px]", isLight ? "bg-white/20 text-white" : "bg-muted text-on-dark-muted")}>
                  {studio.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className={cn(
                "transition-all duration-200 underline underline-offset-2 group-hover/studio:underline-offset-4",
                isLight ? "decoration-white/40 group-hover/studio:decoration-white/70" : "decoration-on-dark/30 group-hover/studio:decoration-on-dark/60"
              )}>
                {studio.name}
              </span>
            </Badge>
          </Link>
        ) : address ? (
          <a
            href={buildMapsUrl(address)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex"
          >
            <Badge variant={isLight ? "outline" : "secondary"} className={isLight ? "border-white/30 text-white hover:bg-white/10 cursor-pointer" : "hover:bg-accent cursor-pointer"}>
              <MapPin className="h-3 w-3 mr-1" />
              <span className={isLight ? "underline underline-offset-2 decoration-white/50" : "underline underline-offset-2 decoration-on-dark/50"}>{location}</span>
            </Badge>
          </a>
        ) : location ? (
          <Badge variant={isLight ? "outline" : "secondary"} className={isLight ? "border-white/30 text-white" : ""}>
            <MapPin className="h-3 w-3 mr-1" />
            {location}
          </Badge>
        ) : null)}
        {!hideLocation && room && (
          <Badge variant={isLight ? "outline" : "secondary"} className={isLight ? "border-white/30 text-white" : ""}>
            <DoorOpen className="h-3 w-3 mr-1" />
            {room}
          </Badge>
        )}
        {!hidePrice && (
          <Badge variant={isLight ? "outline" : "secondary"} className={isLight ? "border-white/30 text-white" : ""}>
            {priceStr}
          </Badge>
        )}
      </div>
    </>
  );
}
